import { useCallback, useEffect, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { sendMessagePost } from "../../../../packages/shared/src/messages.ts";
import {
  markOutboxFailed,
  markOutboxSending,
  markOutboxSent,
  nextMessageOutboxItem,
  queueMessagePost,
  reconcileMessageOutbox,
  retryOutboxItem,
  type MessageOutboxItem,
} from "./message-outbox.ts";

export interface UseMessageOutboxResult {
  readonly networkOnline: boolean;
  readonly outbox: readonly MessageOutboxItem[];
  readonly queuePost: (threadId: string, body: string) => void;
  readonly reconcile: (posts: readonly { readonly postId: string }[]) => void;
  readonly retryPost: (clientPostId: string) => void;
}

export function createMessageClientPostId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
}

function browserIsOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

export function useMessageOutbox(options: {
  readonly accountId: string;
  readonly client: SupabaseClient;
  readonly createClientPostId?: () => string;
  readonly officerId: string;
  readonly onSent: () => void;
}): UseMessageOutboxResult {
  const [networkOnline, setNetworkOnline] = useState(browserIsOnline);
  const networkOnlineRef = useRef(networkOnline);
  const [outbox, setOutbox] = useState<readonly MessageOutboxItem[]>([]);
  const outboxRef = useRef(outbox);
  const flushingRef = useRef(false);
  const generationRef = useRef(0);
  const mountedRef = useRef(true);
  const onSentRef = useRef(options.onSent);
  onSentRef.current = options.onSent;
  const createClientPostIdRef = useRef(options.createClientPostId ?? createMessageClientPostId);
  createClientPostIdRef.current = options.createClientPostId ?? createMessageClientPostId;

  const apply = useCallback((next: readonly MessageOutboxItem[]) => {
    outboxRef.current = next;
    setOutbox(next);
  }, []);

  const flush = useCallback(async () => {
    if (flushingRef.current || !networkOnlineRef.current) return;
    flushingRef.current = true;
    const generation = generationRef.current;
    try {
      for (;;) {
        if (!mountedRef.current || generationRef.current !== generation || !networkOnlineRef.current) return;
        const next = nextMessageOutboxItem(
          outboxRef.current,
          options.accountId,
          options.officerId,
          networkOnlineRef.current,
        );
        if (next === null) return;
        apply(markOutboxSending(outboxRef.current, next.clientPostId));
        const result = await sendMessagePost(options.client, {
          accountId: options.accountId,
          body: next.body,
          clientPostId: next.clientPostId,
          threadId: next.threadId,
        });
        if (!mountedRef.current || generationRef.current !== generation) return;
        if (!result.ok) {
          apply(markOutboxFailed(outboxRef.current, next.clientPostId, result.reason));
          return;
        }
        apply(markOutboxSent(outboxRef.current, next.clientPostId, result.value));
        onSentRef.current();
      }
    } finally {
      if (generationRef.current === generation) {
        flushingRef.current = false;
      }
    }
  }, [apply, options.accountId, options.client, options.officerId]);

  useEffect(() => {
    generationRef.current += 1;
    flushingRef.current = false;
    apply([]);
  }, [apply, options.accountId, options.officerId]);

  useEffect(() => {
    const onOnline = () => {
      networkOnlineRef.current = true;
      setNetworkOnline(true);
      void flush();
    };
    const onOffline = () => {
      networkOnlineRef.current = false;
      setNetworkOnline(false);
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [flush]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
    };
  }, []);

  const queuePost = useCallback((threadId: string, body: string) => {
    const item = queueMessagePost({
      accountId: options.accountId,
      officerId: options.officerId,
      threadId,
      clientPostId: createClientPostIdRef.current(),
      body,
      createdAt: new Date().toISOString(),
      networkOnline: networkOnlineRef.current,
    });
    apply([...outboxRef.current, item]);
    if (networkOnlineRef.current) void flush();
  }, [apply, flush, options.accountId, options.officerId]);

  const retryPost = useCallback((clientPostId: string) => {
    apply(retryOutboxItem(outboxRef.current, clientPostId, networkOnlineRef.current));
    if (networkOnlineRef.current) void flush();
  }, [apply, flush]);

  const reconcile = useCallback((posts: readonly { readonly postId: string }[]) => {
    apply(reconcileMessageOutbox(outboxRef.current, posts));
  }, [apply]);

  return { networkOnline, outbox, queuePost, reconcile, retryPost };
}
